import { useEffect, useState } from 'react';

import {
  ActionDialogButtons,
  ActionDialogContent,
  Button,
  ContentDialog,
  Dialog,
  DialogContent,
  FormFeedback,
  FormGroup,
  FormLabel,
  IconButton,
  Input,
  SelectDropdown,
  SingleDatePicker,
  Table,
  TableCard,
  Text,
} from '@opositandopn/ui';
import {
  Form,
  Formik,
  FormikErrors,
  FormikProps,
  validateYupSchema,
  yupToFormErrors,
} from 'formik';
import { FaRunning } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi2';
import {
  IoAddOutline,
  IoCreateOutline,
  IoStopwatchOutline,
  IoTrashOutline,
} from 'react-icons/io5';
import { PiBarbell } from 'react-icons/pi';
import * as Yup from 'yup';

import PullUpsImg from '../../assets/images/087e74c4-0129-4b6b-b1e4-de49df4dfcae.webp';
import RunImg from '../../assets/images/2dbe3828-c0a2-4814-9bf2-17ca30889360.webp';
import ObstacleCourseImg from '../../assets/images/70eef70c-84d4-47f0-b871-0ccdc6b78977.webp';
import EmptyImg from '../../assets/images/af448e87-3b3b-4b01-8ded-cd3d0dbb1927.webp';
import { Page } from '../../components/shared/page/Page';
import { SectionCard } from '../../components/shared/section-card/SectionCard';
import { useIsPremium, useProfile } from '../../context/ProfileContext';
import {
  HangAttemptViewModel,
  ObstacleCourseAttemptViewModel,
  PullUpsAttemptViewModel,
  RunAttemptViewModel,
  useAddHangAttempt,
  useAddObstacleCourseAttempt,
  useAddPullUpsAttempt,
  useAddRunAttempt,
  useDeleteHangAttempt,
  useDeleteObstacleCourseAttempt,
  useDeletePullUpsAttempt,
  useDeleteRunAttempt,
  useEvaluatePhysicalTests,
  useGetHangAttemptsOnDate,
  useGetObstacleCourseAttemptsOnDate,
  useGetPhysicalTestsMetrics,
  useGetPullUpsAttemptsOnDate,
  useGetRunAttemptsOnDate,
  useUpdateHangAttempt,
  useUpdateObstacleCourseAttempt,
  useUpdatePullUpsAttempt,
  useUpdateRunAttempt,
} from '../../features/physical-tests/api/usePhysicalTests';
import { SelectGenderDialog } from '../../features/physical-tests/components/select-gender-dialog/SelectGenderDialog';
import { useAppActions } from '../../state/appStore';

import styles from './PhysicalTestsDailyLogPage.module.scss';

type AddDialog = 'obstacleCourse' | 'strength' | 'run' | null;

interface ObstacleCourseFormValues {
  seconds: number;
  hundredths: number;
  note: string;
}

interface PullUpsFormValues {
  reps: number;
  note: string;
}

interface HangFormValues {
  seconds: number;
  note: string;
}

interface RunFormValues {
  distance: number;
  minutes: number;
  seconds: number;
  note: string;
}

const OBSTACLE_COURSE_NOTES = [
  'Buen ritmo',
  'Muy rápido',
  'Lento',
  'Fluido',
  'Fallo en giro',
  'Resbalón',
  'Mala coordinación',
  'Buena coordinación',
  'Técnica limpia',
  'Técnica mejorable',
  'Descontrolado',
  'Fui de más a menos',
  'Fui de menos a más',
  'Mejor marca',
  'Cerca de mi marca',
  'Peor de lo esperado',
  'Fatiga',
  'Sin energía',
  'Concentrado',
];

const STRENGTH_NOTES = [
  'Fácil',
  'Muy fácil',
  'Al límite',
  'Fallo muscular',
  'Técnica limpia',
  'Técnica regular',
  'Mala técnica',
  'Balanceo',
  'Dominadas estrictas',
  'Lastre +5kg',
  'Lastre +10kg',
  'Lastre +15kg',
  'Peso corporal',
  'Buen agarre',
  'Fallo de agarre',
  'Fatiga',
  'Muy fatigado',
  'Buenas sensaciones',
  'Malas sensaciones',
  'Agujetas',
];

const RUN_NOTES = [
  'Buen ritmo',
  'Ritmo constante',
  'Ritmo alto',
  'Ritmo bajo',
  'Buen pacing',
  'Mal pacing',
  'Fui de más a menos',
  'Fui de menos a más',
  'Sprint final',
  'Sin aire',
  'Buen control respiratorio',
  'Recuperación rápida',
  'Recuperación lenta',
  'Pulsaciones altas',
  'Pulsaciones controladas',
  'Fatiga',
  'Sin energía',
  'Buenas sensaciones',
  'Malas sensaciones',
];

function toNoteOptions(notes: string[], selected: string) {
  return notes.map((note) => ({
    value: note,
    label: note,
    selected: selected === note,
  }));
}

function pointsVariant(
  points: number,
): 'success' | 'warning' | 'danger' | 'neutral' {
  if (points >= 8) return 'success';
  if (points >= 5) return 'warning';
  if (points >= 0) return 'danger';
  return 'neutral';
}

function formatPace(pace: string) {
  const parts = pace.split(':');
  if (parts.length < 3) return `${pace} min/km`;
  const minutes = Number(parts[1]);
  const seconds = Number(parts[2].split('.')[0]);
  return `${minutes}:${String(seconds).padStart(2, '0')} min/km`;
}

function formatTime(time: string, includeMinutes: boolean = true) {
  const parts = time.split(':');
  if (parts.length < 3) return time;
  const [seconds, fractional = ''] = parts[2].split('.');
  if (!includeMinutes) {
    return `${seconds}.${fractional.slice(0, 2).padEnd(2, '0')}`;
  }
  if (fractional === '') return `${parts[1]}:${seconds}`;
  return `${parts[1]}:${seconds}.${fractional.slice(0, 2)}`;
}

function toTimeSpan(minutes: number, seconds: number, hundredths: number = 0) {
  const safeMinutes = Math.max(0, Math.floor(minutes));
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const safeHundredths = Math.max(0, Math.min(99, Math.floor(hundredths)));
  const base = `00:${String(safeMinutes).padStart(2, '0')}:${String(safeSeconds).padStart(2, '0')}`;
  return safeHundredths === 0
    ? base
    : `${base}.${String(safeHundredths).padStart(2, '0')}`;
}

function parseTimeSpan(time: string) {
  const parts = time.split(':');
  if (parts.length < 3) return { minutes: 0, seconds: 0, hundredths: 0 };
  const minutes = Number(parts[1]) || 0;
  const [secs, frac = ''] = parts[2].split('.');
  const seconds = Number(secs) || 0;
  const hundredths = Number(frac.slice(0, 2).padEnd(2, '0')) || 0;
  return { minutes, seconds, hundredths };
}

function PhysicalTestsDailyLogPage() {
  const { profile } = useProfile();
  const { setShowGetPremiumPopup } = useAppActions();
  const isPremium = useIsPremium();
  const { mutateAsync: addObstacleCourse } = useAddObstacleCourseAttempt();
  const { mutateAsync: addPullUps } = useAddPullUpsAttempt();
  const { mutateAsync: addHang } = useAddHangAttempt();
  const { mutateAsync: addRun } = useAddRunAttempt();
  const { mutateAsync: updateObstacleCourse } =
    useUpdateObstacleCourseAttempt();
  const { mutateAsync: updatePullUps } = useUpdatePullUpsAttempt();
  const { mutateAsync: updateHang } = useUpdateHangAttempt();
  const { mutateAsync: updateRun } = useUpdateRunAttempt();
  const { mutate: deleteObstacleCourse } = useDeleteObstacleCourseAttempt();
  const { mutate: deletePullUps } = useDeletePullUpsAttempt();
  const { mutate: deleteHang } = useDeleteHangAttempt();
  const { mutate: deleteRun } = useDeleteRunAttempt();

  const [date, setDate] = useState<Date>(new Date());
  const [showEvaluationDialog, setShowEvaluationDialog] = useState(false);
  const [openDialog, setOpenDialog] = useState<AddDialog>(null);
  const [editingObstacleCourse, setEditingObstacleCourse] =
    useState<ObstacleCourseAttemptViewModel | null>(null);
  const [editingPullUps, setEditingPullUps] =
    useState<PullUpsAttemptViewModel | null>(null);
  const [editingHang, setEditingHang] = useState<HangAttemptViewModel | null>(
    null,
  );
  const [editingRun, setEditingRun] = useState<RunAttemptViewModel | null>(
    null,
  );

  const { data: obstacleCourseAttempts, isLoading: isLoadingObstacleCourse } =
    useGetObstacleCourseAttemptsOnDate({ date });
  const { data: pullUpsAttempts, isLoading: isLoadingPullUps } =
    useGetPullUpsAttemptsOnDate({ date });
  const { data: hangAttempts, isLoading: isLoadingHang } =
    useGetHangAttemptsOnDate({ date });
  const { data: runAttempts, isLoading: isLoadingRun } =
    useGetRunAttemptsOnDate({ date });

  const evaluationFromDate = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 7);
    return d;
  })();

  const { data: metrics, isLoading: isLoadingMetrics } =
    useGetPhysicalTestsMetrics({ fromDate: evaluationFromDate }, !!profile.id);

  const isLoadingAttempts =
    isLoadingObstacleCourse ||
    isLoadingPullUps ||
    isLoadingHang ||
    isLoadingRun ||
    isLoadingMetrics;

  const {
    data: evaluation,
    error: evaluationError,
    isFetching: isEvaluating,
    evaluate,
  } = useEvaluatePhysicalTests(false);

  useEffect(() => {
    if (evaluationError) {
      setShowEvaluationDialog(false);
    }
  }, [evaluationError]);

  const hasMetrics =
    (metrics?.obstacleCourse?.length ?? 0) > 0 ||
    (metrics?.pullUps?.length ?? 0) > 0 ||
    (metrics?.hang?.length ?? 0) > 0 ||
    (metrics?.run?.length ?? 0) > 0;

  const handleEvaluate = () => {
    if (!isPremium) {
      setShowGetPremiumPopup(true);
      return;
    }
    setShowEvaluationDialog(true);
    if (hasMetrics) {
      evaluate();
    }
  };

  const handleAddAttempt = (dialog: AddDialog) => {
    if (!isPremium) {
      setShowGetPremiumPopup(true);
      return;
    }
    setOpenDialog(dialog);
  };

  const closeAddDialog = () => {
    setOpenDialog(null);
    setEditingObstacleCourse(null);
    setEditingPullUps(null);
    setEditingHang(null);
    setEditingRun(null);
  };

  const handleEditObstacleCourse = (
    attempt: ObstacleCourseAttemptViewModel,
  ) => {
    setEditingObstacleCourse(attempt);
    setOpenDialog('obstacleCourse');
  };
  const handleEditPullUps = (attempt: PullUpsAttemptViewModel) => {
    setEditingPullUps(attempt);
    setOpenDialog('strength');
  };
  const handleEditHang = (attempt: HangAttemptViewModel) => {
    setEditingHang(attempt);
    setOpenDialog('strength');
  };
  const handleEditRun = (attempt: RunAttemptViewModel) => {
    setEditingRun(attempt);
    setOpenDialog('run');
  };

  return (
    <Page title="Registro diario">
      <div className={styles['filters']}>
        <Button
          variant="outline-primary"
          onClick={handleEvaluate}
          disabled={isEvaluating || isLoadingAttempts || !profile.id}
          prefixIcon={<HiSparkles className={styles['emoji']} />}
        >
          Analiza tu progreso
        </Button>
        <SingleDatePicker
          initDate={date}
          onChange={(value) => {
            if (value.getTime() !== date.getTime()) {
              setDate(value);
            }
          }}
          locale="es"
          hideTime
          closeOnDateClick
          maxDate={new Date()}
        />
      </div>

      <ContentDialog
        ai
        className={styles['evaluation-dialog']}
        open={showEvaluationDialog}
        warning={
          hasMetrics
            ? 'Este contenido ha sido generado con inteligencia artificial, verifica siempre su exactitud.'
            : undefined
        }
        titleIcon={<HiSparkles className={styles['emoji']} />}
        loading={hasMetrics && isEvaluating}
        onClose={() => setShowEvaluationDialog(false)}
      >
        {!hasMetrics ? (
          <div className={styles['evaluation-empty']}>
            <img src={EmptyImg} alt="" />
            <p>
              Aún no has subido marcas esta semana. Registra tus resultados para
              poder generar una evaluación con inteligencia artificial
            </p>
          </div>
        ) : (
          (evaluation?.evaluation ?? '')
        )}
      </ContentDialog>
      <div className={styles['row']}>
        <SectionCard
          className={styles['card']}
          icon={<IoStopwatchOutline />}
          image={ObstacleCourseImg}
          title="Circuito de agilidad"
          tooltip="Evalúa la agilidad, la coordinación y la capacidad del aspirante para ejecutar movimientos rápidos y cambios de dirección con precisión y eficacia."
          button={
            <Button
              variant="outline-primary"
              suffixIcon={<IoAddOutline />}
              onClick={() => handleAddAttempt('obstacleCourse')}
            >
              Añadir intento
            </Button>
          }
        >
          <TableCard.Root size="sm" className={styles['table']}>
            <Table aria-label="Intentos de circuito">
              <Table.Header>
                <Table.Head label="Ejercicio" isRowHeader />
                <Table.Head label="Tiempo" />
                <Table.Head
                  label="Comentario"
                  style={{
                    width: '100%',
                  }}
                />
                <Table.Head label="Puntos" />
                {isPremium && <Table.Head label="" />}
              </Table.Header>
              <Table.Body renderEmptyState={() => 'No hay intentos'}>
                {(obstacleCourseAttempts ?? []).map((attempt) => (
                  <Table.Row key={attempt.id} className={styles['tableRow']}>
                    <Table.Cell isRowHeader>
                      <strong>Circuito</strong>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        variety="annotation"
                        font="monospace"
                        variant="neutral"
                      >
                        {formatTime(attempt.time, false)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <em>{attempt.note}</em>
                    </Table.Cell>
                    <Table.Cell>
                      <Text variant={pointsVariant(attempt.points)}>
                        <strong>{attempt.points}</strong>
                      </Text>
                    </Table.Cell>
                    {isPremium && (
                      <Table.Cell>
                        <div className={styles['actions']}>
                          <IconButton
                            icon={<IoCreateOutline />}
                            size="sm"
                            aria-label="Editar intento"
                            onClick={() => handleEditObstacleCourse(attempt)}
                          />
                          <IconButton
                            icon={<IoTrashOutline />}
                            size="sm"
                            aria-label="Eliminar intento"
                            onClick={() =>
                              deleteObstacleCourse({ attemptId: attempt.id })
                            }
                          />
                        </div>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </TableCard.Root>
        </SectionCard>
        <SectionCard
          className={styles['card']}
          icon={<PiBarbell />}
          image={PullUpsImg}
          title="Fuerza"
          tooltip="Mide la resistencia muscular del tren superior, especialmente de la musculatura dorsal, los brazos y la zona escapulohumeral, mediante ejercicios como dominadas o suspensión en barra."
          button={
            <Button
              variant="outline-primary"
              suffixIcon={<IoAddOutline />}
              disabled={profile.gender === undefined}
              onClick={() => handleAddAttempt('strength')}
            >
              Añadir intento
            </Button>
          }
        >
          <TableCard.Root size="sm" className={styles['table']}>
            <Table aria-label="Intentos de fuerza">
              <Table.Header>
                <Table.Head label="Ejercicio" isRowHeader />
                <Table.Head label="Marca" />
                <Table.Head
                  label="Comentario"
                  style={{
                    width: '100%',
                  }}
                />
                <Table.Head label="Puntos" />
                {isPremium && <Table.Head label="" />}
              </Table.Header>
              <Table.Body renderEmptyState={() => 'No hay intentos'}>
                {(pullUpsAttempts ?? []).map((attempt) => (
                  <Table.Row key={attempt.id} className={styles['tableRow']}>
                    <Table.Cell isRowHeader>
                      <strong>Dominadas</strong>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        variety="annotation"
                        font="monospace"
                        variant="neutral"
                      >
                        {attempt.reps} reps
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <em>{attempt.note}</em>
                    </Table.Cell>
                    <Table.Cell>
                      <Text variant={pointsVariant(attempt.points)}>
                        <strong>{attempt.points}</strong>
                      </Text>
                    </Table.Cell>
                    {isPremium && (
                      <Table.Cell>
                        <div className={styles['actions']}>
                          <IconButton
                            icon={<IoCreateOutline />}
                            size="sm"
                            aria-label="Editar intento"
                            onClick={() => handleEditPullUps(attempt)}
                          />
                          <IconButton
                            icon={<IoTrashOutline />}
                            size="sm"
                            aria-label="Eliminar intento"
                            onClick={() =>
                              deletePullUps({ attemptId: attempt.id })
                            }
                          />
                        </div>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
                {(hangAttempts ?? []).map((attempt) => (
                  <Table.Row key={attempt.id} className={styles['tableRow']}>
                    <Table.Cell isRowHeader>
                      <strong>Suspensión en barra</strong>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        variety="annotation"
                        font="monospace"
                        variant="neutral"
                      >
                        {formatTime(attempt.time)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <em>{attempt.note}</em>
                    </Table.Cell>
                    <Table.Cell>
                      <Text variant={pointsVariant(attempt.points)}>
                        <strong>{attempt.points}</strong>
                      </Text>
                    </Table.Cell>
                    {isPremium && (
                      <Table.Cell>
                        <div className={styles['actions']}>
                          <IconButton
                            icon={<IoCreateOutline />}
                            size="sm"
                            aria-label="Editar intento"
                            onClick={() => handleEditHang(attempt)}
                          />
                          <IconButton
                            icon={<IoTrashOutline />}
                            size="sm"
                            aria-label="Eliminar intento"
                            onClick={() =>
                              deleteHang({ attemptId: attempt.id })
                            }
                          />
                        </div>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </TableCard.Root>
        </SectionCard>
        <SectionCard
          className={styles['card']}
          icon={<FaRunning />}
          image={RunImg}
          title="Carrera"
          tooltip="Evalúa la resistencia aeróbica y la capacidad cardiovascular del aspirante a través de una carrera continua de media distancia."
          button={
            <Button
              variant="outline-primary"
              suffixIcon={<IoAddOutline />}
              onClick={() => handleAddAttempt('run')}
            >
              Añadir intento
            </Button>
          }
        >
          <TableCard.Root size="sm" className={styles['table']}>
            <Table aria-label="Intentos de carrera">
              <Table.Header>
                <Table.Head label="Ejercicio" isRowHeader />
                <Table.Head label="Distancia" />
                <Table.Head label="Tiempo" />
                <Table.Head label="Ritmo" />
                <Table.Head
                  label="Comentario"
                  style={{
                    width: '100%',
                  }}
                />
                <Table.Head label="Puntos" />
                {isPremium && <Table.Head label="" />}
              </Table.Header>
              <Table.Body renderEmptyState={() => 'No hay intentos'}>
                {(runAttempts ?? []).map((attempt) => (
                  <Table.Row key={attempt.id} className={styles['tableRow']}>
                    <Table.Cell isRowHeader>
                      <strong>Carrera</strong>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        variety="annotation"
                        font="monospace"
                        variant="neutral"
                      >
                        {attempt.distance} m
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        variety="annotation"
                        font="monospace"
                        variant="neutral"
                      >
                        {formatTime(attempt.time)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        variety="annotation"
                        font="monospace"
                        variant="neutral"
                      >
                        {formatPace(attempt.pace)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <em>{attempt.note}</em>
                    </Table.Cell>
                    <Table.Cell>
                      <Text variant={pointsVariant(attempt.points)}>
                        <strong>{attempt.points}</strong>
                      </Text>
                    </Table.Cell>
                    {isPremium && (
                      <Table.Cell>
                        <div className={styles['actions']}>
                          <IconButton
                            icon={<IoCreateOutline />}
                            size="sm"
                            aria-label="Editar intento"
                            onClick={() => handleEditRun(attempt)}
                          />
                          <IconButton
                            icon={<IoTrashOutline />}
                            size="sm"
                            aria-label="Eliminar intento"
                            onClick={() => deleteRun({ attemptId: attempt.id })}
                          />
                        </div>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </TableCard.Root>
        </SectionCard>
      </div>

      <Dialog
        open={openDialog === 'obstacleCourse'}
        onOpenChange={(open) => !open && closeAddDialog()}
      >
        <DialogContent>
          <ActionDialogContent
            className={styles['dialog-content']}
            icon={<IoStopwatchOutline />}
            title="Intento de circuito"
            subtitle="Indica el tiempo que has tardado en completar el circuito."
          >
            <Formik
              enableReinitialize
              initialValues={
                editingObstacleCourse
                  ? {
                      seconds: parseTimeSpan(editingObstacleCourse.time)
                        .seconds,
                      hundredths: parseTimeSpan(editingObstacleCourse.time)
                        .hundredths,
                      note: editingObstacleCourse.note ?? '',
                    }
                  : { seconds: 0, hundredths: 0, note: '' }
              }
              validate={(values: ObstacleCourseFormValues) => {
                const errors: FormikErrors<ObstacleCourseFormValues> = {};

                const validationSchema = Yup.object().shape({
                  seconds: Yup.number()
                    .typeError('Introduce un número válido.')
                    .integer('Los segundos deben ser un número entero.')
                    .min(0, 'Los segundos no pueden ser negativos.')
                    .max(59, 'Los segundos deben ser menores de 60.'),
                  hundredths: Yup.number()
                    .typeError('Introduce un número válido.')
                    .integer('Las centésimas deben ser un número entero.')
                    .min(0, 'Las centésimas no pueden ser negativas.')
                    .max(99, 'Las centésimas deben ser menores de 100.'),
                });

                try {
                  validateYupSchema<ObstacleCourseFormValues>(
                    values,
                    validationSchema,
                    true,
                  );
                } catch (err) {
                  return yupToFormErrors(err);
                }

                if (+values.seconds === 0 && +values.hundredths === 0) {
                  errors.hundredths = 'El tiempo debe ser mayor que 0.';
                }

                return errors;
              }}
              onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                const time = toTimeSpan(0, +values.seconds, +values.hundredths);
                const note = values.note || undefined;
                if (editingObstacleCourse) {
                  await updateObstacleCourse(
                    { attemptId: editingObstacleCourse.id, time, note },
                    { onSuccess: closeAddDialog },
                  );
                } else {
                  await addObstacleCourse(
                    { date, time, note },
                    { onSuccess: closeAddDialog },
                  );
                }
                setSubmitting(false);
              }}
            >
              {({
                values,
                touched,
                errors,
                isSubmitting,
                handleChange,
                handleSubmit,
              }: FormikProps<ObstacleCourseFormValues>) => (
                <Form
                  className={styles['form']}
                  noValidate
                  onSubmit={handleSubmit}
                >
                  <div className={styles['fields-row']}>
                    <FormGroup>
                      <FormLabel>Segundos</FormLabel>
                      <Input
                        id="seconds"
                        name="seconds"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={59}
                        value={values.seconds}
                        onChange={handleChange}
                        error={touched.seconds && !!errors.seconds}
                      />
                      {touched.seconds && errors.seconds && (
                        <FormFeedback variant="danger">
                          {errors.seconds}
                        </FormFeedback>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>Centésimas</FormLabel>
                      <Input
                        id="hundredths"
                        name="hundredths"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={99}
                        value={values.hundredths}
                        onChange={handleChange}
                        error={touched.hundredths && !!errors.hundredths}
                      />
                      {touched.hundredths && errors.hundredths && (
                        <FormFeedback variant="danger">
                          {errors.hundredths}
                        </FormFeedback>
                      )}
                    </FormGroup>
                  </div>
                  <FormGroup>
                    <FormLabel>Nota rápida</FormLabel>
                    <SelectDropdown
                      placeholder="Sin nota"
                      onSelect={(value: string | number) =>
                        handleChange({
                          target: { name: 'note', value: String(value) },
                        })
                      }
                      options={toNoteOptions(
                        OBSTACLE_COURSE_NOTES,
                        values.note,
                      )}
                    />
                  </FormGroup>
                  <ActionDialogButtons>
                    <Button variant="outline-primary" onClick={closeAddDialog}>
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                      loading={isSubmitting}
                    >
                      Enviar
                    </Button>
                  </ActionDialogButtons>
                </Form>
              )}
            </Formik>
          </ActionDialogContent>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog === 'strength'}
        onOpenChange={(open) => !open && closeAddDialog()}
      >
        <DialogContent>
          {profile.gender === 'male' ? (
            <ActionDialogContent
              className={styles['dialog-content']}
              icon={<PiBarbell />}
              title="Intento de dominadas"
              subtitle="Indica el número de dominadas completas que has realizado."
            >
              <Formik
                enableReinitialize
                initialValues={
                  editingPullUps
                    ? {
                        reps: editingPullUps.reps,
                        note: editingPullUps.note ?? '',
                      }
                    : { reps: 0, note: '' }
                }
                validate={(values: PullUpsFormValues) => {
                  const validationSchema = Yup.object().shape({
                    reps: Yup.number()
                      .typeError('Introduce un número válido.')
                      .integer('Las repeticiones deben ser un número entero.')
                      .min(1, 'Debe ser al menos 1 repetición.')
                      .max(100, 'No puede superar 100 repeticiones.'),
                  });

                  try {
                    validateYupSchema<PullUpsFormValues>(
                      values,
                      validationSchema,
                      true,
                    );
                  } catch (err) {
                    return yupToFormErrors(err);
                  }

                  return {};
                }}
                onSubmit={async (values, { setSubmitting }) => {
                  setSubmitting(true);
                  const reps = +values.reps;
                  const note = values.note || undefined;
                  if (editingPullUps) {
                    await updatePullUps(
                      { attemptId: editingPullUps.id, reps, note },
                      { onSuccess: closeAddDialog },
                    );
                  } else {
                    await addPullUps(
                      { date, reps, note },
                      { onSuccess: closeAddDialog },
                    );
                  }
                  setSubmitting(false);
                }}
              >
                {({
                  values,
                  touched,
                  errors,
                  isSubmitting,
                  handleChange,
                  handleSubmit,
                }: FormikProps<PullUpsFormValues>) => (
                  <Form
                    className={styles['form']}
                    noValidate
                    onSubmit={handleSubmit}
                  >
                    <FormGroup>
                      <FormLabel>Repeticiones</FormLabel>
                      <Input
                        id="reps"
                        name="reps"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={100}
                        value={values.reps}
                        onChange={handleChange}
                        error={touched.reps && !!errors.reps}
                      />
                      {touched.reps && errors.reps && (
                        <FormFeedback variant="danger">
                          {errors.reps}
                        </FormFeedback>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>Nota rápida</FormLabel>
                      <SelectDropdown
                        placeholder="Sin nota"
                        onSelect={(value: string | number) =>
                          handleChange({
                            target: { name: 'note', value: String(value) },
                          })
                        }
                        options={toNoteOptions(STRENGTH_NOTES, values.note)}
                      />
                    </FormGroup>
                    <ActionDialogButtons>
                      <Button
                        variant="outline-primary"
                        onClick={closeAddDialog}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                      >
                        Enviar
                      </Button>
                    </ActionDialogButtons>
                  </Form>
                )}
              </Formik>
            </ActionDialogContent>
          ) : (
            <ActionDialogContent
              className={styles['dialog-content']}
              icon={<PiBarbell />}
              title="Intento de suspensión en barra"
              subtitle="Indica el tiempo que has aguantado en suspensión en barra."
            >
              <Formik
                enableReinitialize
                initialValues={
                  editingHang
                    ? {
                        seconds:
                          parseTimeSpan(editingHang.time).minutes * 60 +
                          parseTimeSpan(editingHang.time).seconds,
                        note: editingHang.note ?? '',
                      }
                    : { seconds: 0, note: '' }
                }
                validate={(values: HangFormValues) => {
                  const validationSchema = Yup.object().shape({
                    seconds: Yup.number()
                      .typeError('Introduce un número válido.')
                      .integer('Los segundos deben ser un número entero.')
                      .min(1, 'El tiempo debe ser al menos 1 segundo.')
                      .max(3599, 'El tiempo no puede superar los 59:59.'),
                  });

                  try {
                    validateYupSchema<HangFormValues>(
                      values,
                      validationSchema,
                      true,
                    );
                  } catch (err) {
                    return yupToFormErrors(err);
                  }

                  return {};
                }}
                onSubmit={async (values, { setSubmitting }) => {
                  setSubmitting(true);
                  const totalSeconds = +values.seconds;
                  const time = toTimeSpan(
                    Math.floor(totalSeconds / 60),
                    totalSeconds % 60,
                  );
                  const note = values.note || undefined;
                  if (editingHang) {
                    await updateHang(
                      { attemptId: editingHang.id, time, note },
                      { onSuccess: closeAddDialog },
                    );
                  } else {
                    await addHang(
                      { date, time, note },
                      { onSuccess: closeAddDialog },
                    );
                  }
                  setSubmitting(false);
                }}
              >
                {({
                  values,
                  touched,
                  errors,
                  isSubmitting,
                  handleChange,
                  handleSubmit,
                }: FormikProps<HangFormValues>) => (
                  <Form
                    className={styles['form']}
                    noValidate
                    onSubmit={handleSubmit}
                  >
                    <FormGroup>
                      <FormLabel>Segundos</FormLabel>
                      <Input
                        id="seconds"
                        name="seconds"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={3599}
                        value={values.seconds}
                        onChange={handleChange}
                        error={touched.seconds && !!errors.seconds}
                      />
                      {touched.seconds && errors.seconds && (
                        <FormFeedback variant="danger">
                          {errors.seconds}
                        </FormFeedback>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>Nota rápida</FormLabel>
                      <SelectDropdown
                        placeholder="Sin nota"
                        onSelect={(value: string | number) =>
                          handleChange({
                            target: { name: 'note', value: String(value) },
                          })
                        }
                        options={toNoteOptions(STRENGTH_NOTES, values.note)}
                      />
                    </FormGroup>
                    <ActionDialogButtons>
                      <Button
                        variant="outline-primary"
                        onClick={closeAddDialog}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                      >
                        Enviar
                      </Button>
                    </ActionDialogButtons>
                  </Form>
                )}
              </Formik>
            </ActionDialogContent>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog === 'run'}
        onOpenChange={(open) => !open && closeAddDialog()}
      >
        <DialogContent>
          <ActionDialogContent
            className={styles['dialog-content']}
            icon={<FaRunning />}
            title="Intento de carrera"
            subtitle="Indica la distancia recorrida y el tiempo empleado."
          >
            <Formik
              enableReinitialize
              initialValues={
                editingRun
                  ? {
                      distance: editingRun.distance,
                      minutes: parseTimeSpan(editingRun.time).minutes,
                      seconds: parseTimeSpan(editingRun.time).seconds,
                      note: editingRun.note ?? '',
                    }
                  : { distance: 1000, minutes: 0, seconds: 0, note: '' }
              }
              validate={(values: RunFormValues) => {
                const errors: FormikErrors<RunFormValues> = {};

                const validationSchema = Yup.object().shape({
                  distance: Yup.number()
                    .typeError('Selecciona una distancia válida.')
                    .oneOf(
                      [100, 200, 300, 400, 600, 800, 1000],
                      'Selecciona una distancia válida.',
                    ),
                  minutes: Yup.number()
                    .typeError('Introduce un número válido.')
                    .integer('Los minutos deben ser un número entero.')
                    .min(0, 'Los minutos no pueden ser negativos.')
                    .max(59, 'Los minutos deben ser menores de 60.'),
                  seconds: Yup.number()
                    .typeError('Introduce un número válido.')
                    .integer('Los segundos deben ser un número entero.')
                    .min(0, 'Los segundos no pueden ser negativos.')
                    .max(59, 'Los segundos deben ser menores de 60.'),
                });

                try {
                  validateYupSchema<RunFormValues>(
                    values,
                    validationSchema,
                    true,
                  );
                } catch (err) {
                  return yupToFormErrors(err);
                }

                if (+values.minutes === 0 && +values.seconds === 0) {
                  errors.seconds = 'El tiempo debe ser mayor que 0.';
                }

                return errors;
              }}
              onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                const distance = +values.distance;
                const time = toTimeSpan(+values.minutes, +values.seconds);
                const note = values.note || undefined;
                if (editingRun) {
                  await updateRun(
                    { attemptId: editingRun.id, distance, time, note },
                    { onSuccess: closeAddDialog },
                  );
                } else {
                  await addRun(
                    { date, distance, time, note },
                    { onSuccess: closeAddDialog },
                  );
                }
                setSubmitting(false);
              }}
            >
              {({
                values,
                touched,
                errors,
                isSubmitting,
                handleChange,
                handleSubmit,
              }: FormikProps<RunFormValues>) => (
                <Form
                  className={styles['form']}
                  noValidate
                  onSubmit={handleSubmit}
                >
                  <FormGroup>
                    <FormLabel>Distancia</FormLabel>
                    <SelectDropdown
                      onSelect={(value: string | number) =>
                        handleChange({
                          target: { name: 'distance', value: Number(value) },
                        })
                      }
                      options={[
                        {
                          value: 100,
                          label: '100 m',
                          selected: values.distance === 100,
                        },
                        {
                          value: 200,
                          label: '200 m',
                          selected: values.distance === 200,
                        },
                        {
                          value: 300,
                          label: '300 m',
                          selected: values.distance === 300,
                        },
                        {
                          value: 400,
                          label: '400 m',
                          selected: values.distance === 400,
                        },
                        {
                          value: 600,
                          label: '600 m',
                          selected: values.distance === 600,
                        },
                        {
                          value: 800,
                          label: '800 m',
                          selected: values.distance === 800,
                        },
                        {
                          value: 1000,
                          label: '1000 m',
                          selected: values.distance === 1000,
                        },
                      ]}
                    />
                    {touched.distance && errors.distance && (
                      <FormFeedback variant="danger">
                        {errors.distance}
                      </FormFeedback>
                    )}
                  </FormGroup>
                  <div className={styles['fields-row']}>
                    <FormGroup>
                      <FormLabel>Minutos</FormLabel>
                      <Input
                        id="minutes"
                        name="minutes"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={59}
                        value={values.minutes}
                        onChange={handleChange}
                        error={touched.minutes && !!errors.minutes}
                      />
                      {touched.minutes && errors.minutes && (
                        <FormFeedback variant="danger">
                          {errors.minutes}
                        </FormFeedback>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>Segundos</FormLabel>
                      <Input
                        id="seconds"
                        name="seconds"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={59}
                        value={values.seconds}
                        onChange={handleChange}
                        error={touched.seconds && !!errors.seconds}
                      />
                      {touched.seconds && errors.seconds && (
                        <FormFeedback variant="danger">
                          {errors.seconds}
                        </FormFeedback>
                      )}
                    </FormGroup>
                  </div>
                  <FormGroup>
                    <FormLabel>Nota rápida</FormLabel>
                    <SelectDropdown
                      placeholder="Sin nota"
                      onSelect={(value: string | number) =>
                        handleChange({
                          target: { name: 'note', value: String(value) },
                        })
                      }
                      options={toNoteOptions(RUN_NOTES, values.note)}
                    />
                  </FormGroup>
                  <ActionDialogButtons>
                    <Button variant="outline-primary" onClick={closeAddDialog}>
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                      loading={isSubmitting}
                    >
                      Enviar
                    </Button>
                  </ActionDialogButtons>
                </Form>
              )}
            </Formik>
          </ActionDialogContent>
        </DialogContent>
      </Dialog>

      <SelectGenderDialog />
    </Page>
  );
}

export { PhysicalTestsDailyLogPage };
